import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Container,
  Card,
  CardContent,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { useDispatch, useSelector } from "react-redux";
import { createRedeemOtpRequest } from "../../redux/Redeem/redeemSlice";
import { toast } from "react-toastify";
import SelectStoreDialog from "./SelectStoreDialog";
import LogoImage from "../../Screenshot_4.png";
import { useNavigate, useLocation } from "react-router-dom";
import { getCarouselRequest } from "../../redux/Dashboard/dashboardSlice";
import CarouselSection from "../CarouselSection/CarouselSlider";
import { useAppTheme } from "../../theme/useAppTheme";
import useCurrency from "../../hooks/useCurrency";

const metalTheme = {
  Gold: {
    gradient: "linear-gradient(180deg, #FFD700 0%, #C49B00 100%)",
    shadow: "0 0 15px 4px rgba(156, 39, 176, 0.3)",
    text: "#8B7500",
  },
  Silver: {
    gradient: "linear-gradient(180deg, #E0E0E0 0%, #A9A9A9 100%)",
    shadow: "0 0 15px 4px rgba(156, 39, 176, 0.3)",
    text: "#444",
  },
};

const MetalCard = styled(Box)(({ metalColor }) => ({
  background: metalColor.gradient,
  boxShadow: metalColor.shadow,
  borderRadius: "16px",
  padding: "16px",
  textAlign: "center",
  color: metalColor.text,
  fontWeight: 700,
  marginBottom: "16px",
}));

const CreateRedeemOrder = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { state } = useLocation();
  const row = state?.row;
  const type = state?.type;
  const actualWeight = Number(row?.weight || 0);
  const [storeDialogOpen, setStoreDialogOpen] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);
  const [weight, setWeight] = useState("");
  const [amount, setAmount] = useState("");
  const { bullionRates, appConfig } = useSelector(
    (state) => state.dashboard || {},
  );
  const { loading, otpResponse } = useSelector((state) => state.redeem);
  const { carousel } = useSelector((state) => state.dashboard || {});

  const [topCarousel, setTopCarousel] = React.useState(null);
  const [midCarousel, setMidCarousel] = React.useState(null);
  const [bottomCarousel, setBottomCarousel] = React.useState(null);
  const decimal = appConfig?.decimalPrecision ?? 4;
  const themeColors = useAppTheme();

  const { symbol } = useCurrency();

  useEffect(() => {
    dispatch(getCarouselRequest());
  }, [dispatch]);

  useEffect(() => {
    if (row) {
      setWeight(row.weight || "");
      setAmount(row.amount || "");
    }
  }, [row]);

  useEffect(() => {
    if (carousel?.data?.length > 0) {
      let top = {},
        mid = {},
        bottom = {};

      carousel.data.forEach((e) => {
        switch (e.name) {
          case "redeem_store_top":
            top = {
              slides: e.slides,
              height: parseFloat(e.height) || 200,
              showNavigator: e.shownavigator,
              duration: e.duration,
              enabled: e.enabled,
            };
            break;
          case "redeem_store_middle":
            mid = {
              slides: e.slides,
              height: parseFloat(e.height) || 200,
              showNavigator: e.shownavigator,
              duration: e.duration,
              enabled: e.enabled,
            };
            break;
          case "redeem_store_bottom":
            bottom = {
              slides: e.slides,
              height: parseFloat(e.height) || 200,
              showNavigator: e.shownavigator,
              duration: e.duration,
              enabled: e.enabled,
            };
            break;
          default:
            break;
        }
      });

      setTopCarousel(top);
      setMidCarousel(mid);
      setBottomCarousel(bottom);
    } else {
      setTopCarousel(null);
      setMidCarousel(null);
      setBottomCarousel(null);
    }
  }, [carousel]);

  const metalName = row.metalDetails?.includes("Gold") ? "Gold" : "Silver";
  const metalColor = metalTheme[metalName] || metalTheme.Silver;

  const getCurrentBuyRate = (metalName) => {
    const rateObj = bullionRates?.find(
      (r) =>
        r?.bullion?.metalName?.toLowerCase() === metalName?.toLowerCase() &&
        r?.rateType?.toLowerCase() === "buy",
    );
    return rateObj?.ratePerGram || 0;
  };
  const currentRate = useMemo(
    () => getCurrentBuyRate(metalName),
    [bullionRates, metalName],
  );

  if (!row) {
    return (
      <Container sx={{ mt: 10 }}>
        <Typography variant="h6">No data found.</Typography>
      </Container>
    );
  }

  const handleStoreSelect = (store) => {
    setSelectedStore(store);
    setStoreDialogOpen(false);
  };

  const handleClose = () => navigate(-1);

  const handleRedeem = () => {
    if (!selectedStore) {
      toast.error("Please select a store first.");
      return;
    }
    if (weight !== "" && Number(weight) <= 0) {
      toast.error("Invalid weight entered");
      return;
    }
    if (!weight || !amount) {
      toast.error("Please enter valid weight and amount.");
      return;
    }

    let redeemtype = "Holding";
    if (type?.toLowerCase() === "gift" || type?.toLowerCase() === "gifts") {
      redeemtype = "Gift";
    } else if (type?.toLowerCase() === "sip") {
      redeemtype = "Sip";
    }

    const payload = {
      weight: parseFloat(weight),
      amount: parseFloat(amount),
      metalName,
      branchCode: selectedStore.branchCode,
      orderType: "Redeem",
      purity: row?.bullion?.purity || 999.0,
      redeemtype,
      phone: selectedStore.phone || "",
      sip: redeemtype === "Sip" ? row?._id || "" : "",
      gifts: redeemtype === "Gift" ? row?._id || "" : "",
      onSuccess: () =>
        navigate("/PendingRedeemScreen", {
          state: { id: row?._id, type: type || redeemtype },
        }),
    };

    dispatch(createRedeemOtpRequest(payload));
  };

  return (
    <Box
      sx={{
        background: themeColors.scaffoldGradientColors,
        minHeight: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        p: 2,
      }}
    >
      <Card
        sx={{
          width: "100%",
          maxWidth: 650,
          borderRadius: 4,
          boxShadow: 5,
          p: 3,
          position: "relative",
          background: themeColors.bigContainer,
          border: `1px solid ${themeColors.scaffold}`,
          color: themeColors.text,
        }}
      >
        <IconButton
          onClick={handleClose}
          sx={{
            position: "absolute",
            top: 12,
            right: 12,
            background: themeColors.primary,
            color: themeColors.text,
            "&:hover": {
              background: themeColors.svgColor,
            },
            border: `1px solid ${themeColors.scaffold}`,
          }}
        >
          <CloseIcon />
        </IconButton>

        <Typography
          variant="h5"
          fontWeight={600}
          align="center"
          sx={{ mb: 3, color: themeColors.primary }}
        >
          Redeem Order
        </Typography>

        {topCarousel && (
          <CarouselSection
            slides={topCarousel.slides}
            height={topCarousel.height}
            showNavigator={topCarousel.showNavigator}
            duration={topCarousel.duration}
            enabled={topCarousel.enabled}
          />
        )}

        {/* Violet Inner Box */}
        <CardContent
          sx={{
            background: themeColors.bigContainer,
            border: `1px solid ${themeColors.scaffold}`,
            color: themeColors.text,
            borderRadius: 3,
            p: 3,
          }}
        >
          {/* Metal Details Box */}
          <MetalCard
            metalColor={metalColor}
            sx={{ border: `1px solid ${themeColors.scaffold}` }}
          >
            <Typography variant="h6" fontWeight={700}>
              {metalName.toUpperCase()}
            </Typography>
            <Typography sx={{ fontSize: "14px", mt: 0.5 }}>
              {row?.bullion?.karat || "24"}K • {row?.bullion?.purity || "999"}
            </Typography>
          </MetalCard>

          {/* Balance & Value Box */}
          <Card
            sx={{
              borderRadius: "16px",
              p: 2,
              mb: 2,
              background: themeColors.mediumContainer,
              border: `1px solid ${themeColors.scaffold}`,
              textAlign: "center",
              color: themeColors.text,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Current Balance
                </Typography>
                <Typography variant="h6" fontWeight={700}>
                  {row.weight || "0"} gms
                </Typography>
              </Box>

              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  Value
                </Typography>
                <Typography variant="h6" fontWeight={700}>
                  {symbol}{row.amount || "0"}
                </Typography>
              </Box>
            </Box>
          </Card>

          {midCarousel && (
            <CarouselSection
              slides={midCarousel.slides}
              height={midCarousel.height}
              showNavigator={midCarousel.showNavigator}
              duration={midCarousel.duration}
              enabled={midCarousel.enabled}
            />
          )}

          <Typography align="center" sx={{ mb: 1, fontWeight: 600 }}>
            REDEEM {metalName.toUpperCase()}
          </Typography>

          <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
            <TextField
              label="Grams"
              fullWidth
              value={weight}
              size="small"
              onChange={(e) => {
                if (type?.toLowerCase() === "gift") return;

                const grams = e.target.value;
                const gramDecimalRegex = new RegExp(
                  `^\\d*(\\.\\d{0,${decimal}})?$`,
                );
                if (grams !== "" && !gramDecimalRegex.test(grams)) return;

                if (
                  (type?.toLowerCase() === "sip" ||
                    type?.toLowerCase() === "holding") &&
                  parseFloat(grams) > actualWeight
                ) {
                  toast.error(
                    "Entered grams is greater than available balance",
                  );
                  return;
                }
                setWeight(grams);

                if (!isNaN(grams) && grams !== "") {
                  setAmount((parseFloat(grams) * currentRate).toFixed(decimal));
                } else {
                  setAmount("");
                }
              }}
              InputProps={{
                readOnly: type?.toLowerCase() === "gift",
              }}
              sx={{
                input: { color: themeColors.text },
                label: { color: themeColors.text },
                "& .MuiOutlinedInput-root fieldset": {
                  borderColor: themeColors.scaffold,
                },
              }}
            />

            <SwapHorizIcon sx={{ fontSize: 32, color: themeColors.text }} />

            <TextField
              label="Amount"
              fullWidth
              value={amount}
              size="small"
              onChange={(e) => {
                if (type?.toLowerCase() === "gift") return;

                const amt = e.target.value;
                if (!/^\d*\.?\d*$/.test(amt)) return;

                const calculatedGrams =
                  !isNaN(amt) && amt !== "" ? parseFloat(amt) / currentRate : 0;
                if (
                  (type?.toLowerCase() === "sip" ||
                    type?.toLowerCase() === "holding") &&
                  calculatedGrams > actualWeight
                ) {
                  toast.error("Entered amount exceeds available balance");
                  return;
                }

                setAmount(amt);

                if (!isNaN(amt) && amt !== "") {
                  setWeight(calculatedGrams.toFixed(decimal));
                } else {
                  setWeight("");
                }
              }}
              InputProps={{
                readOnly: type?.toLowerCase() === "gift",
              }}
              sx={{
                input: { color: themeColors.text },
                label: { color: themeColors.text },
                "& .MuiOutlinedInput-root fieldset": {
                  borderColor: themeColors.scaffold,
                },
              }}
            />
          </Box>

          <Typography variant="body2" sx={{ mb: 1 }}>
            Current Price : {symbol}{currentRate || "0"}.00 /gm
          </Typography>

          {/* Store Selection */}
          <Box
            sx={{
              border: `1px solid ${themeColors.scaffold}`,
              borderRadius: "12px",
              p: 2,
              mb: 3,
              background: themeColors.mediumContainer,
              color: themeColors.text,
            }}
          >
            <Box
              sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}
            >
              <Typography variant="body1">Selected Store</Typography>
              <Typography
                variant="body2"
                sx={{
                  color: themeColors.text,
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
                onClick={() => setStoreDialogOpen(true)}
              >
                {selectedStore ? "Change Store" : "Select Store"}
              </Typography>
            </Box>

            {selectedStore ? (
              <>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <img
                    src={selectedStore.logoUrl || LogoImage}
                    alt="store"
                    width={50}
                    height={50}
                    style={{
                      borderRadius: 12,
                      background: themeColors.mediumContainer,
                      color: themeColors.text,
                      border: `1px solid ${themeColors.scaffold}`,
                      padding: 6,
                    }}
                  />
                  <Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      {selectedStore.branchName}
                    </Typography>
                    <Typography variant="body2">
                      Phone: {selectedStore.phone}
                    </Typography>
                    <Typography variant="body2">
                      Code: {selectedStore.branchCode}
                    </Typography>
                  </Box>
                </Box>
                <Typography variant="body2" sx={{ mt: 1, fontSize: "0.9rem" }}>
                  📍 {selectedStore.address1},{selectedStore.address2},
                  {selectedStore.address3} - {selectedStore.pincode}
                </Typography>
              </>
            ) : (
              <Typography variant="body2" sx={{ color: themeColors.text }}>
                No store selected yet.
              </Typography>
            )}
          </Box>

          <Button
            fullWidth
            variant="contained"
            disabled={loading}
            sx={{
              background: themeColors.primary,
              color: themeColors.text,
              "&:hover": {
                background: themeColors.svgColor,
              },
              border: `1px solid ${themeColors.scaffold}`,
              textTransform: "none",
              fontWeight: 700,
              borderRadius: "30px",
              py: 1.2,
            }}
            onClick={handleRedeem}
          >
            {loading ? "Processing..." : "Redeem"}
          </Button>
        </CardContent>
      </Card>

      {bottomCarousel && (
        <CarouselSection
          slides={bottomCarousel.slides}
          height={bottomCarousel.height}
          showNavigator={bottomCarousel.showNavigator}
          duration={bottomCarousel.duration}
          enabled={bottomCarousel.enabled}
        />
      )}

      {/* Dialogs */}
      <SelectStoreDialog
        open={storeDialogOpen}
        onClose={() => setStoreDialogOpen(false)}
        onSelect={handleStoreSelect}
      />
    </Box>
  );
};

export default CreateRedeemOrder;
